import React from "react"
import RcPagination from "rc-pagination"
import locale from "rc-pagination/es/locale/en_US"
import type { StyleProps } from "@mallardbay/lib-react-components"
import {
    IconButton,
    PillButton,
    componentStyles,
    useColors,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft"
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight"

import Ellipsis from "~components/shared/pagination/ellipsis"
import { SEARCH_PAGE_SIZE } from "~config/constants"
import "./index.css"
import COPY from "~config/copy-constants"

enum ActionType {
    PAGE = "page",
    PREV = "prev",
    NEXT = "next",
    JUMP_PREV = "jump-prev",
    JUMP_NEXT = "jump-next",
}

interface Props {
    readonly total: number
    readonly current: number
    readonly onChange: (page: number) => void
}

export default function Pagination({ total, current, onChange }: Props) {
    const styles = useStyles()

    const itemRender = (
        page: number,
        type: ActionType | string,
        element: React.ReactNode
    ) => {
        const value = page
        const isSelected = current === value

        if (type === ActionType.PAGE) {
            return (
                <PillButton
                    size="sm"
                    style={
                        isSelected
                            ? styles.pageButtonSelected
                            : styles.pageButton
                    }
                >
                    {value}
                </PillButton>
            )
        }

        if (type === ActionType.PREV) {
            return (
                <IconButton
                    ariaLabel={COPY.PREVIOUS}
                    icon={<FontAwesomeIcon icon={faChevronLeft} />}
                />
            )
        }

        if (type === ActionType.NEXT) {
            return (
                <IconButton
                    ariaLabel={COPY.NEXT}
                    icon={<FontAwesomeIcon icon={faChevronRight} />}
                />
            )
        }

        if (type === ActionType.JUMP_PREV || type === ActionType.JUMP_NEXT) {
            return <Ellipsis />
        }

        return element
    }

    return (
        <RcPagination
            total={total}
            current={current}
            pageSize={SEARCH_PAGE_SIZE}
            showLessItems
            showPrevNextJumpers
            className="rc-pagination"
            locale={locale}
            itemRender={itemRender}
            onChange={(value) => onChange(value)}
        />
    )
}

function useStyles() {
    const colors = useColors()

    const sharedStyles: StyleProps = {
        width: "30px",
        height: "30px",
    }

    return componentStyles({
        pageButton: {
            ...sharedStyles,
            backgroundColor: colors.bgColor,
            color: colors.text,
        },
        pageButtonSelected: {
            ...sharedStyles,
            backgroundColor: colors.text,
            color: colors.bgColor,
        },
    })
}
